<template>
  <el-dialog
    :title="text.importTitle"
    :visible="visible"
    width="900px"
    @update:visible="$emit('update:visible', $event)">
    <div class="agency-import__bar">
      <el-upload
        class="agency-import__upload"
        action=""
        :accept="accept"
        :auto-upload="false"
        :show-file-list="false"
        :on-change="onPick">
        <el-button size="small" icon="el-icon-folder-opened">{{ text.importPickFile }}</el-button>
      </el-upload>
      <span class="agency-import__file">{{ fileName || '-' }}</span>
      <span class="agency-import__spacer"></span>
      <el-button size="small" type="text" icon="el-icon-download" @click="downloadTemplate">{{
        text.importTemplateAction
      }}</el-button>
      <el-button size="small" :loading="busy" :disabled="!file" @click="preview">{{
        text.importPreviewAction
      }}</el-button>
      <!-- 오류 행이 하나라도 있으면 실제 업로드는 400 이고 **아무것도 저장되지 않는다**(§3-12) -->
      <el-button size="small" type="primary" :loading="busy" :disabled="!canUpload" @click="upload">{{
        text.importUploadAction
      }}</el-button>
    </div>

    <p class="agency-import__hint">{{ text.importFileHint }}</p>

    <template v-if="rows.length > 0">
      <p class="agency-import__summary">
        {{ summaryText }}
        <span v-if="hasError" class="agency-import__warn">{{ text.importHasError }}</span>
      </p>
      <el-table :data="rows" size="mini" border max-height="360" :row-class-name="rowClass">
        <el-table-column :label="text.importRowColumn" width="60" prop="row" align="right"></el-table-column>
        <el-table-column :label="header[0]" width="90" prop="operatorCode"></el-table-column>
        <el-table-column :label="header[1]" width="110" prop="code"></el-table-column>
        <el-table-column :label="header[2]" min-width="150" prop="agencyNm"></el-table-column>
        <el-table-column :label="header[3]" width="100" prop="mgrNm"></el-table-column>
        <el-table-column :label="header[4]" width="130" prop="mobile"></el-table-column>
        <el-table-column :label="text.importResultColumn" width="70" align="center">
          <template slot-scope="scope">
            <el-tag size="mini" :type="scope.row.error ? 'danger' : 'success'">{{ scope.row.resultText }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column :label="text.importMessageColumn" min-width="200" prop="message"></el-table-column>
      </el-table>
    </template>

    <p v-if="serverMessage" class="agency-import__error">{{ serverMessage }}</p>

    <span slot="footer">
      <el-button size="small" @click="$emit('update:visible', false)">{{ text.closeAction }}</el-button>
    </span>
  </el-dialog>
</template>

<script>
import * as XLSX from 'xlsx'
import { httpErrorBody, httpErrorMessage } from '@/plugins/http/policy'
import {
  AGENCY_IMPORT_ACCEPT,
  AGENCY_IMPORT_HEADER,
  AGENCY_IMPORT_PART,
  CALLPOP_FORM_WRITE_CONFIG,
  agencyImportUrl,
  importHasError,
  importRowsOf,
  importSummaryText,
} from '@/utils/callpopOrg'
import { CALLPOP_TEXT } from '@/utils/callpopText'

/** 업로드 요청의 config — 폼 쓰기 옵트아웃에 multipart 헤더를 더한다(파일은 JSON 이 아니다) */
const IMPORT_CONFIG = Object.assign({ headers: { 'Content-Type': 'multipart/form-data' } }, CALLPOP_FORM_WRITE_CONFIG)

/**
 * 관리점 엑셀 일괄 등록 (§3-12 · 브리프 §4).
 *
 * 흐름은 **미리 보기 → 업로드** 둘이다. 미리 보기(`dryRun=true`)는 저장 없이 같은 응답을 주고, 실제 업로드는
 * **전부-아니면-전무**라 오류 행이 있으면 400 이고 아무것도 저장되지 않는다. 그래서 오류가 0일 때만 업로드
 * 버튼이 살아난다 — 그래도 400 이 오면 같은 표에 오류 행이 다시 그려진다(서버가 정본이다).
 *
 * 양식 파일은 **헤더 배열 하나**([AGENCY_IMPORT_HEADER])에서 만든다 — 문자열 사본을 두 곳에 두지 않는다.
 */
export default {
  name: 'AgencyImportDialog',

  props: {
    visible: { type: Boolean, default: false },
  },

  data() {
    return {
      text: CALLPOP_TEXT,
      header: AGENCY_IMPORT_HEADER,
      accept: AGENCY_IMPORT_ACCEPT,
      file: null,
      fileName: '',
      result: null,
      busy: false,
      serverMessage: '',
    }
  },

  computed: {
    rows() {
      return importRowsOf(this.result)
    },
    hasError() {
      return importHasError(this.result)
    },
    summaryText() {
      return importSummaryText(this.result && this.result.summary)
    },
    /** 업로드는 **미리 보기를 지난 뒤** 오류가 0일 때만 — 눌러 보기 전에는 무엇이 저장될지 모른다 */
    canUpload() {
      return !!this.file && !!this.result && !this.hasError
    },
  },

  watch: {
    visible(open) {
      if (!open) return
      this.file = null
      this.fileName = ''
      this.result = null
      this.serverMessage = ''
    },
  },

  methods: {
    onPick(picked) {
      this.file = (picked && picked.raw) || null
      this.fileName = (picked && picked.name) || ''
      // 파일이 바뀌면 앞 파일의 결과는 이 파일의 사실이 아니다
      this.result = null
      this.serverMessage = ''
    },

    rowClass({ row }) {
      return row && row.error ? 'agency-import__row--error' : ''
    },

    /** 헤더 1행만 든 양식 — 서버가 강제하는 순서 그대로다 */
    downloadTemplate() {
      const sheet = XLSX.utils.aoa_to_sheet([AGENCY_IMPORT_HEADER.slice()])
      const book = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(book, sheet, '관리점')
      XLSX.writeFile(book, this.text.importTemplateFileName)
    },

    preview() {
      return this.send(true)
    },

    upload() {
      return this.send(false)
    },

    /**
     * 미리 보기와 업로드가 **같은 요청**이다 — `dryRun` 하나만 다르다.
     *
     * **400 도 결과다**: 서버가 같은 `rows` 를 실어 주므로 그 본문을 그대로 표에 그린다. 종전에는
     * 인터셉터가 본문을 버려서 같은 파일로 미리 보기를 한 번 더 돌아야 했다 — 요청이 하나 늘고,
     * 되돈 응답은 **그 400 을 만든 바로 그 실행이 아니었다**(그 사이 다른 사람이 같은 코드를 썼다면
     * 다른 행이 보인다). 이제 본문이 오므로 되돌지 않는다.
     */
    async send(dryRun) {
      if (!this.file) {
        this.serverMessage = this.text.importNoFile

        return
      }

      const form = new FormData()
      form.append(AGENCY_IMPORT_PART, this.file)

      this.busy = true
      this.serverMessage = ''

      try {
        const response = await this.$axios.post(agencyImportUrl(dryRun), form, IMPORT_CONFIG)

        this.result = response.data
        if (!dryRun) {
          this.$message({ type: 'success', message: this.text.importDone })
          this.$emit('imported', this.result)
        }
      } catch (error) {
        // 400 의 본문에 같은 `rows` 가 들어 있다 — 그 실행의 결과를 그대로 보인다
        const body = httpErrorBody(error)
        if (body && Array.isArray(body.rows)) this.result = body
        this.serverMessage = httpErrorMessage(error, this.text.loadFailed)
      } finally {
        this.busy = false
      }
    },
  },
}
</script>

<style scoped>
.agency-import__bar {
  align-items: center;
  display: flex;
  gap: 8px;
}
.agency-import__upload {
  display: inline-block;
}
.agency-import__file {
  color: #606266;
  font-size: 12px;
}
.agency-import__spacer {
  flex: 1;
}
.agency-import__hint,
.agency-import__summary {
  color: #909399;
  font-size: 12px;
  margin: 8px 0;
}
.agency-import__warn {
  color: #e6a23c;
  margin-left: 8px;
}
.agency-import__error {
  color: #f56c6c;
  font-size: 12px;
  margin: 8px 0 0;
}
</style>

<style>
.agency-import__row--error {
  background: #fef0f0;
}
</style>
